import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { CreateButton, ExportButton, TopToolbar, useListContext } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { importKindOf, importPath, type ImportKind } from './dataImportApi.ts';

// Opens the import screen on this list's tab. Sized like react-admin's own
// toolbar buttons so it sits quietly beside Create and Export.
export const ImportButton: React.FC<{ kind: ImportKind }> = ({ kind }) => (
  <Button
    component={RouterLink}
    to={importPath(kind)}
    size="small"
    startIcon={<UploadFileOutlinedIcon />}
  >
    Import
  </Button>
);

// react-admin's default list toolbar, plus Import.
export const ListActionsWithImport: React.FC<{ kind: ImportKind }> = ({ kind }) => {
  const { total } = useListContext();
  return (
    <TopToolbar>
      <CreateButton />
      <ExportButton disabled={total === 0} />
      <ImportButton kind={kind} />
    </TopToolbar>
  );
};

// A list with nothing in it yet: add one, or bring in a spreadsheet of them.
export const ImportEmpty: React.FC<{ kind: ImportKind }> = ({ kind }) => {
  const { many } = importKindOf(kind);
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
      <Typography variant="h6">No {many} yet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Add one, or import a spreadsheet of them.
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="center"
        sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}
      >
        <CreateButton variant="contained" />
        <Button
          component={RouterLink}
          to={importPath(kind)}
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
        >
          Import {many}
        </Button>
      </Stack>
    </Box>
  );
};
